import React, { useState, useEffect, memo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Alert,
  ActivityIndicator
} from 'react-native';
import { theme } from '../constants/theme';
import { hp, wp } from '../helpers/common';
import moment from 'moment';
import { Heart, Comment, Edit, Delete, ThreeDotsHorizontal } from '../../assets/icons';
import Loading from './Loading';
import Avatar from './Avatar';
import { useAuth } from '../Utils/AuthProvider';
import {
  fetchPollDetails,
  voteOnPoll,
  removeVote,
  hasUserVoted,
  getUserVotes,
  deletePoll
} from '../services/pollService';

import defaultUserImg from '../../assets/images/defaultuser.png';

const PollCard = memo(({
  item,
  currentUser,
  showMoreIcon = true,
  hasShadow = true,
  showDelete = false,
  onDelete = () => {},
  onEdit = () => {},
  onPress = () => {}
}) => {
  const { userData } = useAuth();
  const [pollData, setPollData] = useState(item);
  const [userVotes, setUserVotes] = useState([]);
  const [hasVoted, setHasVoted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [voting, setVoting] = useState(false);

  useEffect(() => {
    loadPollData();
  }, [item.id]);

  const loadPollData = async () => {
    setLoading(true);
    try {
      const [detailsResult, votedResult, votesResult] = await Promise.all([
        fetchPollDetails(item.id),
        hasUserVoted(item.id, currentUser?.id),
        getUserVotes(item.id, currentUser?.id)
      ]);

      if (detailsResult.success) {
        setPollData(detailsResult.data);
      }

      if (votedResult.success) {
        setHasVoted(votedResult.data);
      }

      if (votesResult.success) {
        setUserVotes(votesResult.data);
      }
    } catch (error) {
      console.error('Error loading poll data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (optionId) => {
    if (voting) return;
    
    setVoting(true);
    try {
      const isCurrentlyVoted = userVotes.includes(optionId);
      
      let result;
      if (isCurrentlyVoted) {
        // Remove vote
        result = await removeVote(pollData.id, optionId, currentUser?.id);
        if (result.success) {
          setUserVotes(prev => prev.filter(id => id !== optionId));
        }
      } else {
        // Add vote
        const optionIds = pollData.allow_multiple_choices 
          ? [...userVotes, optionId]
          : [optionId];
        
        result = await voteOnPoll(pollData.id, optionIds, currentUser?.id);
        if (result.success) {
          setUserVotes(optionIds);
        }
      }

      if (result.success) {
        // Reload poll data to get updated results
        loadPollData();
      } else {
        Alert.alert('Error', result.msg);
      }
    } catch (error) {
      Alert.alert('Error', 'Could not vote');
    } finally {
      setVoting(false);
    }
  };

  const handleDelete = () => {
    Alert.alert(
      'Delete Poll',
      'Are you sure you want to delete this poll?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            const result = await deletePoll(pollData.id, currentUser?.id);
            if (result.success) {
              onDelete(pollData.id);
            } else {
              Alert.alert('Error', result.msg);
            }
          }
        }
      ]
    );
  };

  const getTotalVotes = () => {
    return pollData.results?.reduce((total, result) => total + parseInt(result.vote_count), 0) || 0;
  };

  const getVotePercentage = (optionId) => {
    const result = pollData.results?.find(r => r.option_id === optionId);
    return result ? Math.round(parseFloat(result.percentage)) : 0;
  };

  const getOptionVoteCount = (optionId) => {
    const result = pollData.results?.find(r => r.option_id === optionId);
    return result ? parseInt(result.vote_count) : 0;
  };

  const isExpired = () => {
    return pollData.expires_at && moment(pollData.expires_at).isBefore(moment());
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <View style={[styles.container, hasShadow && styles.shadow]}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.userInfo}>
          <Avatar
            source={pollData.user?.image ? { uri: pollData.user.image } : defaultUserImg}
            size={40}
          />
          <View style={styles.userDetails}>
            <Text style={styles.userName}>{pollData.user?.name || 'Unknown User'}</Text>
            <Text style={styles.time}>
              {moment(pollData.created_at).fromNow()}
              {pollData.community && ` • ${pollData.community.name}`}
            </Text>
          </View>
        </View>
        
        {showMoreIcon && (
          <TouchableOpacity onPress={() => Alert.alert('Options', 'More options coming soon')}>
            <ThreeDotsHorizontal width={20} height={20} color={theme.colors.gray} />
          </TouchableOpacity>
        )}
      </View>

      {/* Poll Question */}
      <TouchableOpacity onPress={onPress} activeOpacity={0.7}>
        <Text style={styles.question}>{pollData.question}</Text>
        {pollData.description && (
          <Text style={styles.description}>{pollData.description}</Text>
        )}
        
        {/* Total Votes - Show at top */}
        <View style={styles.totalVotesContainer}>
          <Text style={styles.totalVotesText}>
            Total Votes: {getTotalVotes()}
          </Text>
        </View>
        
        {/* Poll Options */}
        <View style={styles.optionsContainer}>
          {pollData.poll_options?.map((option, index) => {
            const isVoted = userVotes.includes(option.id);
            const percentage = getVotePercentage(option.id);
            const optionVotes = getOptionVoteCount(option.id);
            
            return (
              <TouchableOpacity
                key={option.id}
                style={[
                  styles.optionButton,
                  isVoted && styles.optionVoted,
                  isExpired() && styles.optionDisabled
                ]}
                onPress={() => !isExpired() && handleVote(option.id)}
                disabled={isExpired() || voting}
              >
                <View style={styles.optionContent}>
                  <View style={[
                    styles.percentageBar,
                    { width: `${percentage}%` }
                  ]} />
                  
                  <View style={styles.optionTextContainer}>
                    <Text style={[
                      styles.optionText,
                      isVoted && styles.optionTextVoted,
                      isExpired() && styles.optionTextDisabled
                    ]}>
                      {option.option_text}
                    </Text>
                    
                    <Text style={styles.optionStats}>
                      {percentage}% • {optionVotes} {optionVotes !== 1 ? 'votes' : 'vote'}
                    </Text>
                  </View>
                  
                  {isVoted && (
                    <View style={styles.checkmark}>
                      <Text style={styles.checkmarkText}>✓</Text>
                    </View>
                  )}
                </View>
              </TouchableOpacity>
            );
          })}
        </View>

        {/* Poll Stats */}
        <View style={styles.statsContainer}>
          <Text style={styles.statsText}>
            {pollData.expires_at && (
              <Text>
                {isExpired() ? 'Expired' : `Expires ${moment(pollData.expires_at).fromNow()}`}
              </Text>
            )}
          </Text>
          
          {pollData.allow_multiple_choices && (
            <Text style={styles.multipleChoiceText}>Multiple choice</Text>
          )}
        </View>
      </TouchableOpacity>

      {/* Actions */}
      <View style={styles.actions}>
        {showDelete && pollData.userId === currentUser?.id && (
          <TouchableOpacity onPress={handleDelete} style={styles.actionButton}>
            <Delete width={20} height={20} color={theme.colors.red} />
            <Text style={[styles.actionText, { color: theme.colors.red }]}>Delete</Text>
          </TouchableOpacity>
        )}
        
        {pollData.userId === currentUser?.id && (
          <TouchableOpacity onPress={onEdit} style={styles.actionButton}>
            <Edit width={20} height={20} color={theme.colors.primary} />
            <Text style={[styles.actionText, { color: theme.colors.primary }]}>Edit</Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.colors.white,
    borderRadius: 12,
    padding: 16,
    marginVertical: 8,
    marginHorizontal: 16,
  },
  shadow: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  userInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  userDetails: {
    marginLeft: 12,
    flex: 1,
  },
  userName: {
    fontSize: hp(1.8),
    fontWeight: '600',
    color: theme.colors.dark,
  },
  time: {
    fontSize: hp(1.4),
    color: theme.colors.textLight,
    marginTop: 2,
  },
  question: {
    fontSize: hp(2),
    fontWeight: '600',
    color: theme.colors.dark,
    marginBottom: 8,
    lineHeight: 24,
  },
  description: {
    fontSize: hp(1.6),
    color: theme.colors.textLight,
    marginBottom: 16,
    lineHeight: 20,
  },
  totalVotesContainer: {
    marginBottom: 12,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: theme.colors.primaryLight,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: theme.colors.primary,
  },
  totalVotesText: {
    fontSize: hp(1.6),
    fontWeight: '600',
    color: theme.colors.primary,
  },
  optionsContainer: {
    marginBottom: 12,
  },
  optionButton: {
    marginBottom: 8,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: theme.colors.gray,
    borderWidth: 1,
    borderColor: theme.colors.grayDark,
  },
  optionVoted: {
    backgroundColor: theme.colors.primaryLight,
    borderWidth: 2,
    borderColor: theme.colors.primary,
  },
  optionDisabled: {
    opacity: 0.6,
  },
  optionContent: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    position: 'relative',
  },
  percentageBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    backgroundColor: theme.colors.primary,
    opacity: 0.2,
  },
  optionTextContainer: {
    flex: 1,
    zIndex: 1,
  },
  optionText: {
    fontSize: hp(1.7),
    color: theme.colors.dark,
    fontWeight: '700',
  },
  optionTextVoted: {
    color: theme.colors.primary,
    fontWeight: '700',
  },
  optionTextDisabled: {
    color: theme.colors.gray,
    fontWeight: '700',
  },
  optionStats: {
    fontSize: hp(1.3),
    color: theme.colors.textLight,
    marginTop: 2,
    fontWeight: '500',
  },
  checkmark: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: theme.colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 8,
  },
  checkmarkText: {
    color: theme.colors.white,
    fontSize: hp(1.4),
    fontWeight: 'bold',
  },
  statsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  statsText: {
    fontSize: hp(1.4),
    color: theme.colors.textLight,
  },
  multipleChoiceText: {
    fontSize: hp(1.3),
    color: theme.colors.primary,
    fontWeight: '500',
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 16,
    padding: 8,
  },
  actionText: {
    fontSize: hp(1.4),
    marginLeft: 4,
    fontWeight: '500',
  },
});

export default PollCard;
